/**
 * 量表自测接口
 * @author haoru
 */
import { IVideo } from '@/schemas/video';
import { checkResponseCode } from '@/utils/helpers';
import { ApiConfigs, Response } from '@schemas/service';
import {
  ISurveyInfo,
  ISurveyPageableList,
  ISurveyQuestion,
  ISurveyReportInfo,
  TDepartments,
  TypeSurveySubmitInput,
  IRecordCardInfo,
  ISurveyRecordDetail
} from '@schemas/survey';
import { request } from './request';

const api: ApiConfigs = {
  querySurveyList: {
    url: '/health/api/v1/measure/querySurveyList',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/7769/health/api/v1/measure/querySurveyList',
    method: 'GET'
  },

  querySurveyDetail: {
    url: '/health/api/v1/measure/querySurveyDetail',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/7769/health/api/v1/measure/querySurveyDetail',
    method: 'GET'
  },

  queryQuestionList: {
    url: '/health/api/v1/measure/queryQuestionList',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/7769/health/api/v1/measure/queryQuestionList',
    method: 'GET'
  },

  submitRecord: {
    url: '/health/api/v1/measure/submitRecord',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/7769/health/api/v1/measure/submitRecord',
    method: 'POST'
  },

  queryRecordDetail: {
    url: '/health/api/v1/measure/queryRecordDetail',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/7769/health/api/v1/measure/queryRecordDetail',
    method: 'GET'
  },

  querySurveyHealthVideo: {
    // url: 'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/measure/querySurveyHealthVideo',
    url: '/health/api/v1/measure/querySurveyHealthVideo',
    method: 'GET'
  },

  queryDepartmentList: {
    url: '/health/api/v1/measure/queryDepartmentList',
    method: 'GET'
  },

  querySurveyRecordsList: {
    url: '/health/api/v1/measure/queryRecordDetailList',
    method: 'GET'
  },

  querySurveyRecordData: {
    url: '/health/api/v1/measure/querySurveyAnswerByRecordId',
    method: 'GET'
  }
};

const survey = {
  async querySurveyList({ departmentId, pageNum, pageSize }, options = {}) {
    const response: Response = await request(
      api.querySurveyList,
      {
        departmentId,
        pageNum,
        pageSize
      },
      null,
      options
    );
    const { result = {} } = response;
    return result as ISurveyPageableList;
  },

  async querySurveyDetail({ surveyCode }) {
    const response: Response = await request(api.querySurveyDetail, {
      surveyCode
    });
    const { result = {} } = response;
    return result as ISurveyInfo;
  },

  async queryQuestionList({ surveyCode }) {
    const response: Response = await request(api.queryQuestionList, {
      surveyCode
    });
    const { result = [] } = response;
    return result as ISurveyQuestion[];
  },

  async submitRecord(input: TypeSurveySubmitInput) {
    const response: Response = await request(api.submitRecord, {}, input, {
      timeout: 5000
    });
    const { result = {} } = response;
    return result as ISurveyReportInfo;
  },

  async queryRecordDetail({ surveyCode }) {
    const response: Response = await request(
      api.queryRecordDetail,
      {
        surveyCode
      },
      undefined,
      { hideGlobalLoading: false }
    );
    const { result = {} } = response;
    return result as ISurveyReportInfo;
  },

  async querySurveyHealthVideo({ pageNum, pageSize, surveyCode }) {
    try {
      const response: Response<{
        lastMark: boolean;
        list: IVideo[];
        pageNum: number;
        pageSize: number;
        total: number;
      }> = await request(api.querySurveyHealthVideo, {
        pageNum,
        pageSize,
        surveyCode
      });
      const { result } = response;

      if (result) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async queryDepartmentList() {
    try {
      const response: Response = await request(
        api.queryDepartmentList,
        undefined,
        undefined,
        { hideGlobalLoading: false }
      );
      const { code, result } = response;
      if (checkResponseCode(code)) {
        return result as TDepartments;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // 获取量表测评记录数据
  async querySurveyRecordsList({ pageNum, pageSize }) {
    try {
      const response: Response = await request(api.querySurveyRecordsList, {
        pageNum,
        pageSize
      });
      const { result } = response;

      return result
        ? (result as {
            lastMark: boolean;
            list: IRecordCardInfo[];
            pageNum: number;
            pageSize: number;
            total: number;
          })
        : Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // 获取量表测评记录详情
  async querySurveyRecordData({ recordId }) {
    try {
      const response: Response = await request(
        api.querySurveyRecordData,
        {
          recordId
        },
        undefined,
        { hideGlobalLoading: false }
      );
      const { result } = response;
      return result ? (result as ISurveyRecordDetail) : Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  }
};

export default survey;
